import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  TextField,
  LinearProgress,
  Chip,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import axios from "axios";
import FUNCTIONS_URLS from '../../../../Config/functions';


const INVESTIGATE_URL = FUNCTIONS_URLS.InvestigateTransactionsWithoutLedgersHttp;

const typeLabel = (type) => ({ 0: "Deposit", 1: "Withdrawal", 2: "Performance" }[type] ?? `Type ${type}`);

const TransactionsVsLedgersTool = () => {
  const [userEmailFilter, setUserEmailFilter] = useState("");
  const [limit, setLimit] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);

  const runInvestigation = async () => {
    setError(null);
    setResult(null);
    setLoading(true);
    setProgress(20);
    try {
      const body = {};
      if (userEmailFilter && userEmailFilter.trim()) body.userEmail = userEmailFilter.trim();
      if (limit !== "" && !isNaN(Number(limit)) && Number(limit) > 0) body.limit = Number(limit);
      const response = await axios.post(INVESTIGATE_URL, body, {
        headers: { "Content-Type": "application/json" },
        timeout: 300000,
      });
      setProgress(100);
      setResult(response.data);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Investigation failed";
      setError(msg);
      Swal.fire({
        icon: "error",
        title: "Investigation failed",
        text: msg,
      });
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const summary = result?.summary;
  const usersWithMissing = result?.usersWithMissing || [];
  const usersProcessed = result?.usersProcessed ?? 0;

  return (
    <Box>
      <Typography variant="h6" sx={{ color: "#2af598", mb: 2 }}>
        Transactions vs Ledgers (read-only)
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Checks whether every approved/paid transaction has a matching ledger (by doc_id or ledger doc id). Use this to decide if summary/Data Comparison can use ledgers-only for count and total. No data is modified.
      </Alert>

      <Card sx={{ mb: 2, bgcolor: "rgba(42, 245, 152, 0.05)", border: "1px solid rgba(42, 245, 152, 0.2)" }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              label="Filter by user email (optional)"
              size="small"
              value={userEmailFilter}
              onChange={(e) => setUserEmailFilter(e.target.value)}
              sx={{ minWidth: 280, "& .MuiOutlinedInput-root": { color: "#eee" }, "& label": { color: "#aaa" } }}
            />
            <TextField
              label="Limit users (optional)"
              size="small"
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              sx={{ width: 120, "& .MuiOutlinedInput-root": { color: "#eee" }, "& label": { color: "#aaa" } }}
            />
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              onClick={runInvestigation}
              disabled={loading}
              sx={{ bgcolor: "#2af598", color: "#111", "&:hover": { bgcolor: "#25d885" } }}
            >
              Run investigation
            </Button>
          </Box>
          {progress > 0 && progress < 100 && (
            <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Card sx={{ mb: 2, bgcolor: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ color: "#2af598", mb: 2 }}>
              Global summary
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
              <Chip label={`Users processed: ${usersProcessed}`} sx={{ bgcolor: "rgba(42,245,152,0.2)", color: "#2af598" }} />
              <Chip label={`Users with missing ledgers: ${summary?.usersWithMissing ?? 0}`} sx={{ bgcolor: summary?.usersWithMissing > 0 ? "rgba(255,152,0,0.2)" : "rgba(42,245,152,0.2)", color: summary?.usersWithMissing > 0 ? "#ff9800" : "#2af598" }} />
              <Chip label={`Approved/Paid txns: ${summary?.totalApprovedPaid ?? 0}`} sx={{ bgcolor: "rgba(42,245,152,0.2)", color: "#2af598" }} />
              <Chip label={`With ledger: ${summary?.totalWithLedger ?? 0}`} sx={{ bgcolor: "rgba(42,245,152,0.2)", color: "#2af598" }} />
              <Chip label={`Without ledger: ${summary?.totalWithoutLedger ?? 0}`} sx={{ bgcolor: (summary?.totalWithoutLedger ?? 0) > 0 ? "rgba(255,87,34,0.2)" : "rgba(42,245,152,0.2)", color: (summary?.totalWithoutLedger ?? 0) > 0 ? "#ff5722" : "#2af598" }} />
            </Box>
            {summary?.byType && (
              <Typography variant="body2" sx={{ color: "#aaa", mb: 2 }}>
                By type — Deposit: with {summary.byType[0]?.with ?? 0} / without {summary.byType[0]?.without ?? 0} · Withdrawal: with {summary.byType[1]?.with ?? 0} / without {summary.byType[1]?.without ?? 0} · Performance: with {summary.byType[2]?.with ?? 0} / without {summary.byType[2]?.without ?? 0}
              </Typography>
            )}

            {usersWithMissing.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ color: "#aaa", mt: 2, mb: 1 }}>
                  Users with at least one approved/paid transaction without ledger
                </Typography>
                <TableContainer component={Paper} sx={{ bgcolor: "transparent" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#aaa", width: 48 }} />
                        <TableCell sx={{ color: "#aaa" }}>Email</TableCell>
                        <TableCell sx={{ color: "#aaa" }}>Approved/Paid</TableCell>
                        <TableCell sx={{ color: "#aaa" }}>Missing ledger</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usersWithMissing.map((u) => (
                        <React.Fragment key={u.userId}>
                          <TableRow>
                            <TableCell sx={{ color: "#aaa" }}>
                              <IconButton size="small" onClick={() => setExpandedUser(expandedUser === u.userId ? null : u.userId)}>
                                {expandedUser === u.userId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ color: "#ccc" }}>{u.email || u.userId}</TableCell>
                            <TableCell sx={{ color: "#ccc" }}>{u.approvedPaidCount}</TableCell>
                            <TableCell sx={{ color: "#ff9800" }}>{u.missingCount}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 0, borderBottom: "none" }}>
                              <Collapse in={expandedUser === u.userId}>
                                <Box sx={{ py: 1, pl: 2 }}>
                                  {(u.missing || []).slice(0, 20).map((m, i) => (
                                    <Typography key={i} variant="caption" component="div" sx={{ color: "#888", fontFamily: "monospace" }}>
                                      {m.id} · {typeLabel(m.request_type)} · ${m.amount} · {m.date ? String(m.date).split("T")[0] : "—"}
                                    </Typography>
                                  ))}
                                  {(u.missing?.length || 0) > 20 && (
                                    <Typography variant="caption" sx={{ color: "#666" }}>
                                      ... and {(u.missing?.length || 0) - 20} more
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {result.success && (summary?.totalWithoutLedger ?? 0) === 0 && usersWithMissing.length === 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                All approved/paid transactions in scope have a matching ledger.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TransactionsVsLedgersTool;
